import { RootState } from '../../../../store/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const API_HOST = process.env.REACT_APP_API_HOST;

export interface Report {
  _id?: string;
  title?: string;
  description?: string;
  type?: string;
  data?: any;
  createdAt?: string;
  [key: string]: any;
}

export interface ReportFilter {
  collectionID?: string;
  moduleID?: string;
  startDate?: string;
  endDate?: string;
  type?: string;
  [key: string]: any;
}

/**
 * Report API (RTK Query)
 */
export const reportApi = createApi({
  reducerPath: 'reportApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_HOST,
    credentials: 'include',
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info?.api_token;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Report'],
  endpoints: (build) => ({
    getReports: build.query<{ data: Report[]; total: number }, { page?: number; limit?: number; sortBy?: string; sortOrder?: string }>({
      query: (params) => {
        const query = new URLSearchParams();
        if (params.page) query.append('page', params.page.toString());
        if (params.limit) query.append('limit', params.limit.toString());
        if (params.sortBy) query.append('sortBy', params.sortBy);
        if (params.sortOrder) query.append('sortOrder', params.sortOrder);

        return {
          url: `report/list?${query.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Report'],
    }),

    getReportById: build.query<Report, string>({
      query: (id) => ({
        url: `report/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Report', id }],
    }),

    deleteReport: build.mutation<void, string>({
      query: (id) => ({
        url: `/dashboard/report/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Report'],
    }),

    getCollections: build.query<any[], void>({
      query: () => ({
        url: 'collection/list?limit=99999',
        method: 'GET',
      }),
      providesTags: ['Report'],
    }),

    getModulesByCollection: build.query<any[], string>({
      query: (collectionId) => ({
        url: `module/collection/${collectionId}`,
        method: 'GET',
      }),
      providesTags: ['Report'],
    }),

    getArticles: build.query<{ data: any[]; total: number }, { page?: number; limit?: number; sortBy?: string; sortOrder?: string }>({
      query: (params) => {
        const query = new URLSearchParams();
        if (params.page) query.append('page', params.page.toString());
        if (params.limit) query.append('limit', params.limit.toString());
        if (params.sortBy) query.append('sortBy', params.sortBy);
        if (params.sortOrder) query.append('sortOrder', params.sortOrder);

        return {
          url: `article/list?${query.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Report'],
    }),
  }),
});

export const {
  useGetReportsQuery,
  useGetReportByIdQuery,
  useDeleteReportMutation,
  useGetCollectionsQuery,
  useGetModulesByCollectionQuery,
  useGetArticlesQuery,
} = reportApi;

